import { Icon } from "@/components/icon";
import { useSettings } from "@/store/settingStore";
import { themeVars } from "@/theme/theme.css";
import { rgbAlpha } from "@/utils/theme";
import { Toaster } from "sonner";
import styled from "styled-components";

/**
 * https://sonner.emilkowal.ski/getting-started
 */
export default function Toast() {
	const { themeMode } = useSettings();

	return (
		<ToasterStyleWrapper>
			<Toaster
				position="top-right"
				theme={themeMode}
				toastOptions={{
					duration: 3000,
					style: {
						backgroundColor: themeVars.colors.background.paper as string,
					},
					classNames: {
						toast: "rounded-lg border-0",
						description: "text-xs text-current/45",
						content: "flex-1 ml-6",
						icon: "flex items-center justify-center rounded-lg",
						success: "bg-success/10",
						error: "bg-error/10",
						warning: "bg-warning/10",
						info: "bg-info/10",
					},
				}}
				icons={{
					success: (
						<div className="p-2 bg-success/10 rounded-lg">
							<Icon icon="carbon:checkmark-filled" size={24} color={themeVars.colors.palette.success.default as string} />
						</div>
					),
					error: (
						<div className="p-2 bg-error/10 rounded-lg">
							<Icon icon="carbon:warning-hex-filled" size={24} color={themeVars.colors.palette.error.default as string} />
						</div>
					),
					warning: (
						<div className="p-2 bg-warning/10 rounded-lg">
							<Icon icon="carbon:warning-alt-filled" size={24} color={themeVars.colors.palette.warning.default as string} />
						</div>
					),
					info: (
						<div className="p-2 bg-info/10 rounded-lg">
							<Icon icon="carbon:information-filled" size={24} color={themeVars.colors.palette.info.default as string} />
						</div>
					),
					loading: (
						<div className="p-2 bg-gray-400/10 text-gray-400 rounded-lg">
							<Icon icon="svg-spinners:6-dots-scale-middle" size={24} speed={3} />
						</div>
					),
				}}
				expand
			/>
		</ToasterStyleWrapper>
	);
}

const ToasterStyleWrapper = styled.div`
  [data-sonner-toast] {
    font-weight: 600;
    font-size: 14px;

    [data-cancel] {
      color: ${themeVars.colors.text.primary as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.text as any).primaryChannel as string, 0.08)};
      }
    }

    /* Default */
    [data-action] {
      color: ${themeVars.colors.palette.primary.default as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.palette.primary as any).defaultChannel as string, 0.08)};
      }
    }

    /* Info */
    &[data-type="info"] [data-action] {
      color: ${themeVars.colors.palette.info.default as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.palette.info as any).defaultChannel as string, 0.08)};
      }
    }

    /* Error */
    &[data-type="error"] [data-action] {
      color: ${themeVars.colors.palette.error.default as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.palette.error as any).defaultChannel as string, 0.08)};
      }
    }

    /* Success */
    &[data-type="success"] [data-action] {
      color: ${themeVars.colors.palette.success.default as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.palette.success as any).defaultChannel as string, 0.08)};
      }
    }

    /* Warning */
    &[data-type="warning"] [data-action] {
      color: ${themeVars.colors.palette.warning.default as string};
      background-color: transparent;
      &:hover {
        background-color: ${rgbAlpha((themeVars.colors.palette.warning as any).defaultChannel as string, 0.08)};
      }
    }

    /* Loading */
    &[data-type="loading"] [data-icon] .sonner-loader[data-visible="true"] {
      margin-left: 12px;
    }

    /* Close Button - Hidden */
    [data-close-button] {
      display: none !important;
    }
    
    /* Make toast clickable to dismiss */
    [data-sonner-toast] {
      cursor: pointer;
    }
  }
`;
